import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Topbar from './components/Topbar.js';
import { AuthProvider, useAuth } from './ptcm/context/AuthContext.jsx';
import { FamilyProvider, useFamily } from './ptcm/context/FamilyContext.jsx';
import { ProgressProvider, useProgress } from './ptcm/context/ProgressContext.jsx';
import { QuestionnaireDataProvider } from './ptcm/context/QuestionnaireDataContext.jsx';
import { RecoveryEmailPromptProvider, useRecoveryEmailPrompt } from './ptcm/context/RecoveryEmailPromptContext.jsx';
import { DeleteAccountProvider, useDeleteAccount } from './ptcm/context/DeleteAccountContext.jsx';

import LoginPage from './ptcm/pages/LoginPage.jsx';
import SignupPage from './ptcm/pages/SignupPage.jsx';
import HomePage from './ptcm/pages/HomePage.jsx';
import QuestionnairePage from './ptcm/pages/QuestionnairePage.jsx';
import CategoryQuestionnairePage from './ptcm/pages/CategoryQuestionnairePage.jsx';
import StatsPage from './ptcm/pages/StatsPage.jsx';
import VerifyRecoveryEmailPage from './ptcm/pages/VerifyRecoveryEmailPage.jsx';
import MagicLoginPage from './ptcm/pages/MagicLoginPage.jsx';
import SupportPage from './ptcm/pages/SupportPage.jsx';
import PrivacyPolicyPage from './ptcm/pages/PrivacyPolicyPage.jsx';
import AdminDashboardPage from './ptcm/pages/AdminDashboardPage.jsx';
import BottomNav from './ptcm/components/BottomNav.jsx';
import RecoveryEmailPromptSheet from './ptcm/components/RecoveryEmailPromptSheet.jsx';
import DeleteAccountSheet from './ptcm/components/DeleteAccountSheet.jsx';
import { useEmbeddedMode, withEmbeddedQuery } from './ptcm/hooks/useEmbeddedMode.js';
import useOnboardingStatus from './ptcm/hooks/useOnboardingStatus.js';

function FullScreenLoader() {
  return (
    <div className="flex items-center justify-center h-screen bg-ptcm-bg dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4">
        <div className="w-11 h-11 border-[3px] border-ptcm-primary/30 border-t-ptcm-primary rounded-full animate-spin"></div>
        <div className="text-xl font-semibold tracking-tight text-ptcm-ink dark:text-gray-200">Chargement...</div>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, isLoading } = useAuth();
  const embedded = useEmbeddedMode();
  if (isLoading) return <FullScreenLoader />;
  if (!user) return <Navigate to={withEmbeddedQuery('/', embedded)} replace />;
  return children;
}

function RequireFamily({ children }) {
  const { family, isLoading } = useFamily();
  const embedded = useEmbeddedMode();
  if (isLoading) return <FullScreenLoader />;
  if (!family) return <Navigate to={withEmbeddedQuery('/', embedded)} replace />; // Redirect to home/onboarding if no family
  return children;
}

function AppShell() {
  const { token, user, logout, isLoading: authLoading } = useAuth();
  const { family, members, isLoading: familyLoading } = useFamily();
  const { error: progressError, hasResolvedProgress, status: progressStatus } = useProgress();
  const { canRegisterRecoveryEmail, showRecoveryReminder, openPrompt, recoveryEmailStatus } = useRecoveryEmailPrompt();
  const { open: openDeleteAccount } = useDeleteAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const myMember = useMemo(
    () => (members || []).find((member) => member.uuid === user?.uuid) || null,
    [members, user?.uuid]
  );
  const { hasCompletedCategory } = useOnboardingStatus({
    token,
    family,
    member: myMember
  });
  const embedded = useEmbeddedMode();
  const homePath = withEmbeddedQuery('/', embedded);
  const isStaticPage = location.pathname === '/assistance' || location.pathname === '/confidentialite';
  const shellNeedsBootstrap = Boolean(
    user &&
    (
      familyLoading ||
      (family && (
        !myMember ||
        (!hasResolvedProgress && progressStatus !== 'error' && !progressError)
      ))
    )
  );
  const showBottomNav = Boolean(
    !embedded &&
    !isStaticPage &&
    user &&
    family &&
    hasResolvedProgress &&
    hasCompletedCategory
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleNativeCommand = (event) => {
      const detail = event?.detail;

      // Native shell (iOS/Android) asks to start the account deletion flow.
      if (detail?.type === 'open-delete-account') {
        openDeleteAccount();
        return;
      }

      if (detail?.type !== 'navigate' || typeof detail.path !== 'string' || !detail.path) {
        return;
      }

      navigate(withEmbeddedQuery(detail.path, embedded));
    };

    window.addEventListener('ptcm-native-command', handleNativeCommand);
    return () => window.removeEventListener('ptcm-native-command', handleNativeCommand);
  }, [embedded, navigate, openDeleteAccount]);

  useEffect(() => {
    if (!embedded || typeof window === 'undefined' || !window.ReactNativeWebView?.postMessage) {
      return;
    }

    const payload = {
      type: 'ptcm-shell-state',
      payload: {
        canLogout: Boolean(user),
        currentPath: `${location.pathname}${location.search}${location.hash}`,
        needsRecoveryAttention: Boolean(canRegisterRecoveryEmail),
        recoveryEmailStatus,
        userInitial: (user?.name || user?.email || 'P').slice(0, 1).toUpperCase()
      }
    };

    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
  }, [
    embedded,
    canRegisterRecoveryEmail,
    location.hash,
    location.pathname,
    location.search,
    recoveryEmailStatus,
    user
  ]);

  if (authLoading) {
    return <FullScreenLoader />;
  }

  if (shellNeedsBootstrap) {
    return <FullScreenLoader />;
  }

  return (
    <div
      className="App h-[100dvh] overflow-hidden flex flex-col bg-ptcm-bg dark:bg-gray-900 transition-colors duration-200"
      style={{
        '--ptcm-topbar-h': '56px',
        '--ptcm-bottom-nav-h': '64px',
        '--ptcm-sticky-top-offset': '8px'
      }}
    >
      {!embedded ? (
        <Topbar
          currentUser={user}
          logout={user ? logout : null}
          needsRecoveryEmail={showRecoveryReminder}
          recoveryEmailStatus={recoveryEmailStatus}
          onOpenRecoveryEmailPrompt={() => openPrompt('topbar_cta')}
        />
      ) : null}
      <main
        id="app-scroll-root"
        className="flex-1 w-full p-0 m-0 overflow-y-auto overscroll-y-contain"
        style={{
          paddingBottom: showBottomNav ? 'calc(var(--ptcm-bottom-nav-h) + env(safe-area-inset-bottom))' : undefined,
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/join/:inviteCode" element={<HomePage />} />

          <Route path="/login" element={user ? <Navigate to={homePath} replace /> : <LoginPage />} />
          <Route path="/signup" element={user ? <Navigate to={homePath} replace /> : <SignupPage />} />
          <Route path="/assistance" element={<SupportPage />} />
          <Route path="/confidentialite" element={<PrivacyPolicyPage />} />
          <Route path="/verify-email" element={<VerifyRecoveryEmailPage />} />
          <Route path="/magic-login" element={<MagicLoginPage />} />
          <Route path="/backoffice" element={<AdminDashboardPage />} />
          <Route path="/admin" element={<Navigate to={withEmbeddedQuery('/backoffice', embedded)} replace />} />

          <Route
            path="/questionnaire"
            element={
              <RequireAuth>
                <RequireFamily>
                  <QuestionnairePage />
                </RequireFamily>
              </RequireAuth>
            }
          />
          <Route
            path="/questionnaire/:categoryId"
            element={
              <RequireAuth>
                <RequireFamily>
                  <CategoryQuestionnairePage />
                </RequireFamily>
              </RequireAuth>
            }
          />

          <Route
            path="/results"
            element={
              <RequireAuth>
                <RequireFamily>
                  <StatsPage />
                </RequireFamily>
              </RequireAuth>
            }
          />

          <Route path="*" element={<Navigate to={homePath} replace />} />
        </Routes>
      </main>
      <RecoveryEmailPromptSheet />
      <DeleteAccountSheet />
      {showBottomNav ? <BottomNav /> : null}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <FamilyProvider>
          <ProgressProvider>
            <QuestionnaireDataProvider>
              <RecoveryEmailPromptProvider>
                <DeleteAccountProvider>
                  <AppShell />
                </DeleteAccountProvider>
              </RecoveryEmailPromptProvider>
            </QuestionnaireDataProvider>
          </ProgressProvider>
        </FamilyProvider>
      </AuthProvider>
    </Router>
  );
}
